import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastType } from '../components/ui/Toast';
import ToastContainer from '../components/ui/ToastContainer';
import { haptics } from '@/services/capacitor';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
  logoSrc?: string;
}

export interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string, duration?: number, persistent?: boolean) => string;
  showSuccess: (title: string, message?: string, persistent?: boolean) => void;
  showError: (title: string, message?: string, persistent?: boolean) => void;
  showWarning: (title: string, message?: string, persistent?: boolean) => void;
  showInfo: (title: string, message?: string, persistent?: boolean) => void;
  loading: (title: string, message?: string) => string;
  dismiss: (toastId?: string) => void;
  removeAll: () => void;
  custom: (content: React.ReactNode, variant?: ToastType, duration?: number) => string;
  promise: <T>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ) => Promise<T>;
  confirm: (
    title: string,
    message?: string,
    options?: {
      confirmText?: string;
      cancelText?: string;
      variant?: ToastType;
    }
  ) => Promise<boolean>;
  // Alias methods for convenience (backward compatible)
  success: (title: string, message?: string, persistent?: boolean) => void;
  error: (title: string, message?: string, persistent?: boolean) => void;
  warning: (title: string, message?: string, persistent?: boolean) => void;
  info: (title: string, message?: string, persistent?: boolean) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  logoSrc?: string; // Configurable logo source for all toasts
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  logoSrc = '/logo-no-text.svg' // Trade Loop logo (no text version)
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = () => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Haptic feedback for outcomes (Card 20 follow-up).
   *
   * `@capacitor/haptics` was installed and `services/capacitor` exported a complete
   * `haptics` helper that NOTHING ever called — the app never vibrated, while Card 20's
   * native-capability list claimed it did. Writing that into App Review notes would have
   * been a false claim a reviewer could disprove in one tap.
   *
   * It is wired HERE rather than at individual call sites because `showToast` is the one
   * funnel every user-visible outcome already passes through — success, error and warning
   * from every page, plus `promise()` and `custom()`. One place to reason about, no
   * chance of a surface being missed, and nothing for a future call site to remember.
   *
   * `info` deliberately gets nothing: it covers `loading()` and progress messages, which
   * are not outcomes, and buzzing on them would turn haptics into noise. `haptics.*`
   * already no-ops on web and swallows its own errors, so this cannot affect a render.
   */
  const hapticForToast = (type: ToastType) => {
    if (type === 'success') void haptics.success();
    else if (type === 'error') void haptics.error();
    else if (type === 'warning') void haptics.warning();
  };

  const showToast = useCallback((
    type: ToastType,
    title: string,
    message?: string,
    duration: number = type === 'error' ? 6000 : 4000,
    persistent: boolean = false
  ): string => {
    const id = generateId();
    const newToast: Toast = {
      id,
      type,
      title,
      message,
      duration: persistent ? 0 : duration,
      persistent,
      logoSrc // Use the configurable logo
    };

    setToasts(prev => [...prev, newToast]);
    hapticForToast(type);
    return id;
  }, [logoSrc]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('success', title, message, 4000, persistent);
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('error', title, message, 6000, persistent);
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('warning', title, message, 5000, persistent);
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('info', title, message, 4000, persistent);
  }, [showToast]);

  const loading = useCallback((title: string, message?: string): string => {
    return showToast('info', title, message, 0, true);
  }, [showToast]);

  const dismiss = useCallback((toastId?: string) => {
    if (toastId) {
      dismissToast(toastId);
    } else {
      // Dismiss the most recent toast
      setToasts(prev => prev.slice(0, -1));
    }
  }, [dismissToast]);

  const removeAll = useCallback(() => {
    setToasts([]);
  }, []);

  const custom = useCallback((content: React.ReactNode, variant: ToastType = 'info', duration: number = 4000): string => {
    // For custom content, we'll convert it to a string representation
    const title = typeof content === 'string' ? content : 'Custom notification';
    return showToast(variant, title, undefined, duration, false);
  }, [showToast]);

  const promise = useCallback(<T,>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ): Promise<T> => {
    const loadingId = loading(msgs.loading);

    return promise
      .then((data) => {
        dismiss(loadingId);
        const successMsg = typeof msgs.success === 'function' ? msgs.success(data) : msgs.success;
        showSuccess(successMsg);
        return data;
      })
      .catch((error) => {
        dismiss(loadingId);
        const errorMsg = typeof msgs.error === 'function' ? msgs.error(error) : msgs.error;
        showError(errorMsg);
        throw error;
      });
  }, [loading, dismiss, showSuccess, showError]);

  const confirm = useCallback((
    title: string,
    message?: string,
    options?: {
      confirmText?: string;
      cancelText?: string;
      variant?: ToastType;
    }
  ): Promise<boolean> => {
    // This is a simplified implementation - in a real app, you'd want a proper modal
    // For now, we'll use the browser's confirm dialog and show a toast
    return new Promise((resolve) => {
      const confirmText = options?.confirmText || 'Confirm';
      const cancelText = options?.cancelText || 'Cancel';
      const fullMessage = message ? `${title}\n\n${message}` : title;

      const result = window.confirm(`${fullMessage}\n\n${confirmText} / ${cancelText}`);

      if (result) {
        showSuccess('Confirmed', title);
      } else {
        showInfo('Cancelled', title);
      }

      resolve(result);
    });
  }, [showSuccess, showInfo]);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    loading,
    dismiss,
    removeAll,
    custom,
    promise,
    confirm,
    // Alias methods for convenience (backward compatible)
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
